// src/hooks/useGeneticScores.ts
import { useQuery } from "@tanstack/react-query";

import { fetchDietScores, fetchExerciseScores } from "../../api/geneCodes/GeneticScoringApi";
import { DietScoresResponse, ExerciseScoresResponse } from "../../types/geneCodes/GeneticScoringTypes";

export const GENETIC_SCORES_QUERY_KEY = "genetic-scores";

// Module level so `data ?? EMPTY` keeps the same reference between renders.
const EMPTY_DIET: DietScoresResponse = { success: false, sample_id: "", scores: {} };
const EMPTY_EXERCISE: ExerciseScoresResponse = { success: false, sample_id: "", scores: {} };

export interface UseGeneticScoresResult {
	diet: DietScoresResponse;
	exercise: ExerciseScoresResponse;
	isLoading: boolean;
	isError: boolean;
	error: Error | null;
}

// Both endpoints in one hook: the wheel always renders diet and exercise together.
export const useGeneticScores = (sampleId: string): UseGeneticScoresResult => {
	const enabled = Boolean(sampleId);

	const diet = useQuery<DietScoresResponse, Error>({
		queryKey: [GENETIC_SCORES_QUERY_KEY, "diet", sampleId],
		queryFn: () => fetchDietScores(sampleId),
		enabled,
	});

	const exercise = useQuery<ExerciseScoresResponse, Error>({
		queryKey: [GENETIC_SCORES_QUERY_KEY, "exercise", sampleId],
		queryFn: () => fetchExerciseScores(sampleId),
		enabled,
	});

	return {
		diet: diet.data ?? EMPTY_DIET,
		exercise: exercise.data ?? EMPTY_EXERCISE,
		isLoading: diet.isLoading || exercise.isLoading,
		isError: diet.isError || exercise.isError,
		error: diet.error ?? exercise.error ?? null,
	};
};
